import React from "react";
import { FlatSlot } from "./FlatSlot";
import { CanvasSlot } from "./CanvasSlot";
import { TemplateSlot } from "./TemplateSlot";
// Wraps every slot in a positioned <div> sized + rotated via CSS transforms.
// The wrapping div sits at the slot centre in layout coords; the rotation
// composition (rotateZ × rotateY × rotateX, in CSS "applied right-to-left"
// notation: rotateX rotateY rotateZ matches the world-frame Z-then-Y-then-X
// order in projection.ts's rotate3d). transform-style: preserve-3d lets
// 3D-rotated children (notably CanvasSlot's box faces) stay co-planar
// with their parent.
//
// Shared perspective is set on the design-viewport ancestor; per-slot
// perspective (sharedPerspective off) means we add perspective() to this
// slot's own transform chain so CSS treats each slot as its own scene.
// Memoised: the parent re-renders on every hover state change, but a slot's
// own content only depends on its props (all stabilised by the parent — see
// the EMPTY_* defaults in PackageGraphics). Without memo, hovering would
// re-run every slot's render, including the DrawTemplate-backed TemplateSlots.
export const Slot = React.memo(function Slot({ slot, urls, cells, cellTags, userResources, layout, placeholders, selected, }) {
    const productId = slot.ref.byPackageProductId;
    const cx = slot.x;
    const cy = slot.y;
    const w = slot.width;
    const h = slot.height;
    const x = cx - w / 2;
    const y = cy - h / 2;
    const rotZ = slot.rotation;
    const rotX = slot.rotationX;
    const rotY = slot.rotationY;
    const sharedPerspective = layout.sharedPerspective !== false;
    const persp = layout.perspective || 1400;
    // Canvas runs its own 3D rotation animation internally (the sway between
    // rotationY and rotationYTo) and builds its own cube of faces, so we
    // don't apply rotateX/Y here — only Z, which is the canvas hanging
    // straight or askew on the wall. Other slot kinds get the full
    // rotation chain. Per-slot perspective is added for non-shared mode.
    const transform = slot.kind === "canvas"
        ? `rotateZ(${rotZ}deg)`
        : sharedPerspective
            ? `rotateX(${rotX}deg) rotateY(${rotY}deg) rotateZ(${rotZ}deg)`
            : `perspective(${persp}px) rotateX(${rotX}deg) rotateY(${rotY}deg) rotateZ(${rotZ}deg)`;
    const shadow = slot.dropShadow;
    // CSS drop-shadow takes an offset + blur + colour. Same model as the SVG
    // filter; the colour is black with the configured opacity.
    const filter = shadow !== null
        ? `drop-shadow(${shadow.dx}px ${shadow.dy}px ${shadow.blur}px rgba(0,0,0,${shadow.opacity}))`
        : undefined;
    return (React.createElement("div", { className: `pg-slot${selected ? " selected" : ""}`, "data-product-id": productId, "data-cx": cx, "data-cy": cy, "data-w": w, "data-h": h, "data-rotation": rotZ, style: {
            position: "absolute",
            left: `${x}px`,
            top: `${y}px`,
            width: `${w}px`,
            height: `${h}px`,
            transformOrigin: "center center",
            transform,
            transformStyle: "preserve-3d",
            filter,
        } }, slot.kind === "canvas" ? (React.createElement(CanvasSlot, { slot: slot, urls: urls })) : slot.kind === "flat-template" ? (React.createElement(TemplateSlot, { slot: slot, userResources: userResources, layout: layout, placeholders: placeholders })) : (React.createElement(FlatSlot, { slot: slot, urls: urls, cells: cells, cellTags: cellTags, layout: layout, placeholders: placeholders }))));
});
